import { SxProps, Theme } from "@mui/material";
import { PromoCampaign } from "helpers/promotionHelpers";
import upgradePlanTokens from "./UpgradePlan.tokens";
import { planPromoThemes, planPromoTokens } from "./PlanPromotions.tokens";

const t = upgradePlanTokens;
const pt = planPromoTokens;

/** Vertical stack that holds every block of the "Choose your plan" view. */
export const upgradeViewSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.section,
};

/** "← Back to plan summary". */
export const backLinkSx: SxProps<Theme> = {
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  gap: t.space.backLinkGap,
  p: 0,
  minWidth: 0,
  // <button> does not inherit the page font on its own.
  fontFamily: "inherit",
  fontSize: t.font.backLink,
  fontWeight: t.weight.strong,
  lineHeight: t.lineHeight.tight,
  color: t.color.body,
  textTransform: "none",
  "&:hover": { color: t.color.ink, bgcolor: "transparent" },
  "&:focus-visible": {
    outline: `2px solid ${t.color.focusRing}`,
    outlineOffset: "2px",
  },
};

/** White rounded panel shared by the seat counter and the seat-update banner. */
export const panelSx = (emphasised: boolean): SxProps<Theme> => ({
  bgcolor: t.color.surface,
  border: emphasised
    ? `${t.border.emphasis} solid ${t.color.accent}`
    : `${t.border.hairline} solid ${t.color.cardBorder}`,
  borderRadius: t.radius.card,
  boxShadow: emphasised ? "none" : t.shadow.card,
  px: t.space.cardPaddingX,
  py: t.space.cardPaddingY,
  display: "flex",
  alignItems: "center",
  gap: t.space.cardGap,
  flexWrap: "wrap",
});

/** Panel title ("How many people will use Inkfree?"). */
export const panelTitleSx: SxProps<Theme> = {
  fontSize: t.font.cardTitle,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
};

/** Panel supporting line under the title. */
export const panelSubSx = (relaxed: boolean): SxProps<Theme> => ({
  fontSize: t.font.cardSub,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: relaxed ? t.lineHeight.blurb : t.lineHeight.tight,
});

/** Column holding the panel title + sub-line. */
export const panelTextColumnSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.cardTextGap,
  minWidth: 0,
};

/** Seat counter: three cells sharing one rounded outline. */
export const seatStepperSx: SxProps<Theme> = {
  ml: "auto",
  display: "flex",
  alignItems: "center",
  border: `${t.border.hairline} solid ${t.color.controlBorder}`,
  borderRadius: t.radius.control,
  overflow: "hidden",
  flexShrink: 0,
};

export const seatStepperButtonSx: SxProps<Theme> = {
  width: t.size.stepperButton,
  height: t.size.stepperButton,
  color: t.color.body,
  fontFamily: "inherit",
  fontSize: t.font.stepperControl,
  fontWeight: t.weight.strong,
  lineHeight: t.lineHeight.tight,
  borderRadius: 0,
  "&:hover": { bgcolor: t.color.stepperHoverBg },
  "&.Mui-disabled": { color: t.color.inactiveCtaText },
  "&:focus-visible": {
    outline: `2px solid ${t.color.focusRing}`,
    outlineOffset: "-2px",
  },
};

export const seatStepperValueSx: SxProps<Theme> = {
  width: t.size.stepperValueWidth,
  height: t.size.stepperButton,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: t.font.stepperValue,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
  borderLeft: `${t.border.hairline} solid ${t.color.stepperDivider}`,
  borderRight: `${t.border.hairline} solid ${t.color.stepperDivider}`,
};

/**
 * Editable seat count — same cell as `seatStepperValueSx`, as a bare input so
 * a large team can be typed instead of clicked up one at a time. The native
 * spinners are hidden: the stepper's own buttons do that job.
 */
export const seatStepperInputSx: SxProps<Theme> = {
  width: t.size.stepperValueWidth,
  height: t.size.stepperButton,
  textAlign: "center",
  fontFamily: "inherit",
  fontSize: t.font.stepperValue,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
  bgcolor: "transparent",
  border: "none",
  borderLeft: `${t.border.hairline} solid ${t.color.stepperDivider}`,
  borderRight: `${t.border.hairline} solid ${t.color.stepperDivider}`,
  p: 0,
  outline: "none",
  MozAppearance: "textfield",
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "&:focus-visible": {
    outline: `2px solid ${t.color.focusRing}`,
    outlineOffset: "-2px",
  },
};

/** Monthly / Annual segmented control. */
export const cycleTrackSx: SxProps<Theme> = {
  alignSelf: "center",
  display: "flex",
  // A campaign puts an offer pill on both segments, which outgrows a phone;
  // wrapping is a no-op at every width the plain toggle fits.
  flexWrap: "wrap",
  justifyContent: "center",
  maxWidth: "100%",
  gap: t.space.toggleTrackGap,
  bgcolor: t.color.toggleTrackBg,
  borderRadius: t.radius.toggleTrack,
  p: t.space.toggleTrackPadding,
};

export const cycleItemSx = (
  active: boolean,
  /** A campaign offer pill sits in the segment — design steps it up. */
  hasPromo = false,
): SxProps<Theme> => ({
  ...(hasPromo
    ? { minHeight: pt.cycle.itemMinHeight, px: pt.cycle.itemPaddingX }
    : { height: t.size.toggleItemHeight, px: t.space.toggleItemPaddingX }),
  display: "flex",
  alignItems: "center",
  gap: t.space.toggleAnnualGap,
  bgcolor: active ? t.color.toggleActiveBg : "transparent",
  borderRadius: t.radius.toggleItem,
  fontFamily: "inherit",
  fontSize: t.font.toggle,
  fontWeight: t.weight.strong,
  lineHeight: t.lineHeight.tight,
  color: active ? t.color.toggleActiveText : t.color.toggleIdleText,
  boxShadow: active ? t.shadow.toggleActive : "none",
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: active ? t.color.toggleActiveBg : "transparent" },
  "&:focus-visible": {
    outline: `2px solid ${t.color.focusRing}`,
    outlineOffset: "-2px",
  },
});

/** "SAVE 25%" chip inside the annual segment. */
export const saveBadgeSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: t.font.saveBadge,
  fontWeight: t.weight.heading,
  lineHeight: t.lineHeight.tight,
  color: t.color.saveBadgeText,
  bgcolor: t.color.saveBadgeBg,
  px: t.space.saveBadgePaddingX,
  py: t.space.saveBadgePaddingY,
  borderRadius: t.radius.pill,
};

/** Filled accent button on the seat-update banner. */
export const seatUpdateButtonSx: SxProps<Theme> = {
  ml: "auto",
  height: t.size.seatCtaHeight,
  minHeight: t.size.seatCtaHeight,
  px: t.space.seatCtaPaddingX,
  py: 0,
  bgcolor: t.color.ctaBg,
  color: t.color.onAccent,
  border: "none",
  borderRadius: t.radius.control,
  fontSize: t.font.seatCta,
  fontWeight: t.weight.strong,
  // MUI's button typography adds tracking; the design's buttons have none.
  letterSpacing: "normal",
  lineHeight: t.lineHeight.tight,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: t.color.ctaBgHover, boxShadow: "none" },
};

/**
 * Plan columns. The cards keep a fixed width and the row centres itself, so
 * three plans sit in the middle instead of hugging the left edge.
 */
export const planGridSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "stretch",
  gap: t.space.planGridGap,
  pt: t.space.planGridTop,
};

export const planCardSx = (
  highlighted: boolean,
  dimmed: boolean,
): SxProps<Theme> => ({
  flex: `0 1 ${t.size.planCardWidth}`,
  maxWidth: t.size.planCardWidth,
  position: "relative",
  bgcolor: t.color.surface,
  border: `${t.border.emphasis} solid ${
    highlighted ? t.color.accent : t.color.cardBorder
  }`,
  borderRadius: t.radius.card,
  p: t.space.planCardPadding,
  display: "flex",
  flexDirection: "column",
  gap: t.space.planCardGap,
  opacity: dimmed ? t.opacity.ineligibleCard : 1,
  boxSizing: "border-box",
});

/** RECOMMENDED pill floated over the card's top border. */
export const recommendedBadgeSx: SxProps<Theme> = {
  position: "absolute",
  top: t.size.recommendedTop,
  left: t.size.recommendedLeft,
  display: "inline-flex",
  alignItems: "center",
  fontSize: t.font.recommended,
  fontWeight: t.weight.heading,
  letterSpacing: t.letterSpacing.recommended,
  lineHeight: t.lineHeight.tight,
  color: t.color.onAccent,
  bgcolor: t.color.ctaBg,
  px: t.space.recommendedPaddingX,
  py: t.space.recommendedPaddingY,
  borderRadius: t.radius.pill,
};

export const planHeadSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.planHeadGap,
};

export const planNameSx: SxProps<Theme> = {
  // Rendered as a heading element; reset the browser's default heading margin.
  m: 0,
  fontSize: t.font.planName,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: t.lineHeight.tight,
};

export const planPriceRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "baseline",
  gap: t.space.planPriceGap,
};

export const planPriceSx: SxProps<Theme> = {
  fontSize: t.font.planPrice,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.price,
  lineHeight: t.lineHeight.tight,
};

export const planPerSx: SxProps<Theme> = {
  fontSize: t.font.planPer,
  fontWeight: t.weight.medium,
  color: t.color.muted,
  lineHeight: t.lineHeight.tight,
};

export const planCycleNoteSx: SxProps<Theme> = {
  fontSize: t.font.planCycleNote,
  fontWeight: t.weight.medium,
  color: t.color.muted,
  lineHeight: t.lineHeight.tight,
};

export const planBlurbSx: SxProps<Theme> = {
  fontSize: t.font.planBlurb,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: t.lineHeight.blurb,
};

/** "N seats × $X = $Y/mo". */
export const planTotalNoteSx: SxProps<Theme> = {
  alignSelf: "flex-start",
  fontSize: t.font.totalNote,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
  bgcolor: t.color.totalNoteBg,
  border: `${t.border.hairline} solid ${t.color.totalNoteBorder}`,
  px: t.space.totalNotePaddingX,
  py: t.space.totalNotePaddingY,
  borderRadius: t.radius.totalNote,
};

/** Seat-rule pill ("1 user" / "Up to 5 users" / "More than 5 users"). */
export const planRulePillSx: SxProps<Theme> = {
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  fontSize: t.font.rulePill,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.rulePill,
  lineHeight: t.lineHeight.tight,
  color: t.color.rulePillText,
  bgcolor: t.color.rulePillBg,
  px: t.space.rulePillPaddingX,
  py: t.space.rulePillPaddingY,
  borderRadius: t.radius.pill,
};

/** Feature list — scrolls past `bulletListMaxHeight` rather than growing. */
export const planBulletListSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.bulletGap,
  flex: 1,
  minHeight: 0,
  maxHeight: t.size.bulletListMaxHeight,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: `${t.color.scrollbarThumb} transparent`,
  "&::-webkit-scrollbar": { width: t.size.scrollbarWidth },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: t.color.scrollbarThumb,
    borderRadius: t.radius.pill,
    border: "none",
  },
};

export const planBulletRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: t.space.bulletIconGap,
};

export const planBulletTextSx: SxProps<Theme> = {
  fontSize: t.font.bullet,
  fontWeight: t.weight.medium,
  color: t.color.ink,
  lineHeight: t.lineHeight.bullet,
};

export const planFooterSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.planFooterGap,
};

/**
 * Plan CTA. `accent` is the recommended plan's filled button; `actionable`
 * switches between the pickable outline and the muted current/unavailable one.
 */
export const planCtaSx = (
  accent: boolean,
  actionable: boolean,
): SxProps<Theme> => ({
  height: t.size.planCtaHeight,
  minHeight: t.size.planCtaHeight,
  width: "100%",
  px: 0,
  py: 0,
  bgcolor: accent ? t.color.ctaBg : t.color.surface,
  color: accent
    ? t.color.onAccent
    : actionable
      ? t.color.ink
      : t.color.inactiveCtaText,
  border: accent
    ? "none"
    : `${t.border.hairline} solid ${
        actionable ? t.color.controlBorder : t.color.inactiveCtaBorder
      }`,
  borderRadius: t.radius.control,
  fontSize: t.font.planCta,
  fontWeight: t.weight.strong,
  // MUI's button typography adds tracking; the design's buttons have none.
  letterSpacing: "normal",
  lineHeight: t.lineHeight.tight,
  textTransform: "none",
  boxShadow: "none",
  boxSizing: "border-box",
  "&:hover": {
    bgcolor: accent ? t.color.ctaBgHover : t.color.surface,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: t.color.inactiveCtaText,
    border: `${t.border.hairline} solid ${t.color.inactiveCtaBorder}`,
  },
});

/** Why the plan can't be picked, under a disabled CTA. */
export const planNoteSx: SxProps<Theme> = {
  fontSize: t.font.planNote,
  fontWeight: t.weight.regular,
  color: t.color.ineligibleNote,
  lineHeight: t.lineHeight.tight,
  textAlign: "center",
};

/** "Compare all plans in detail ↗". */
export const compareLinkSx: SxProps<Theme> = {
  alignSelf: "center",
  display: "inline-flex",
  alignItems: "center",
  gap: t.space.compareLinkGap,
  py: t.space.compareLinkPaddingY,
  fontSize: t.font.compareLink,
  fontWeight: t.weight.strong,
  lineHeight: t.lineHeight.tight,
  color: t.color.accent,
  textDecoration: "none",
  "&:hover": { color: t.color.accentHover },
  "&:focus-visible": {
    outline: `2px solid ${t.color.focusRing}`,
    outlineOffset: "2px",
  },
};

/* ── Seasonal promotion on the plan cards ─────────────────────────────── */

/** Headline price while a campaign discount is priced in. */
export const planPromoPriceSx: SxProps<Theme> = {
  fontSize: pt.card.promoPriceSize,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.price,
  lineHeight: t.lineHeight.tight,
};

/** The regular per-seat price, struck through beside the discounted one. */
export const planStruckPriceSx: SxProps<Theme> = {
  fontSize: pt.card.struckSize,
  fontWeight: t.weight.strong,
  color: pt.color.struckPrice,
  lineHeight: t.lineHeight.tight,
};

/** Total + "% off" pill + renewal line, boxed under the price. */
export const planPromoTotalSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  padding: pt.card.totalPadding,
  borderRadius: pt.card.totalRadius,
  bgcolor: pt.color.promoTotalBg,
  border: `${t.border.hairline} solid ${pt.color.promoTotalBorder}`,
};

export const planPromoTotalLineSx: SxProps<Theme> = {
  fontSize: t.font.totalNote,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  lineHeight: t.lineHeight.tight,
};

export const planPromoRenewSx: SxProps<Theme> = {
  fontSize: t.font.planBlurb,
  color: pt.color.renewText,
  lineHeight: t.lineHeight.blurb,
};

/** "70% off · First year" — on the cards and on the billing-cycle toggle. */
export const promoPillSx = (
  campaign: PromoCampaign,
  idle = false,
  /** Sitting in a row (the cycle toggle) rather than a card's column. */
  inline = false,
): SxProps<Theme> => ({
  alignSelf: inline ? "center" : "flex-start",
  display: "inline-flex",
  alignItems: "center",
  fontSize: pt.card.pillSize,
  fontWeight: t.weight.heading,
  lineHeight: t.lineHeight.tight,
  whiteSpace: "nowrap",
  color: planPromoThemes[campaign].pillText,
  bgcolor: idle ? pt.color.cyclePillIdleBg : planPromoThemes[campaign].pillBg,
  px: t.space.saveBadgePaddingX,
  py: "3px",
  borderRadius: t.radius.pill,
});

/** Availability chip at the top of each plan card. */
export const planChipSx = (
  tone: "current" | "available" | "unavailable",
): SxProps<Theme> => ({
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  fontSize: t.font.chip,
  fontWeight: t.weight.heading,
  letterSpacing: t.letterSpacing.recommended,
  lineHeight: t.lineHeight.tight,
  color: t.color.chip[tone].text,
  bgcolor: t.color.chip[tone].bg,
  px: "9px",
  py: "4px",
  borderRadius: t.radius.pill,
});
